/**
 * chartTheme — shared recharts styling for all finance charts.
 *
 * Enterprise-polish item 5: charts must stop looking like library defaults
 * (harsh solid fills, dashed grids, tiny axes, off-token colours).
 *
 * WHY RAW HEX LIVES HERE (and nowhere else): recharts renders SVG and needs
 * raw colour strings for fill/stroke props — CSS var() is not resolved in SVG
 * presentation attributes. Every hex below maps 1:1 to an existing design
 * token in sogweb/src/styles/tokens.css or the saffron preset in
 * sogweb/src/context/ThemeContext.tsx; the token name is noted per line.
 * Components must import from this file rather than repeating literals.
 */

import type { CSSProperties } from 'react';

/** Semantic series colours. Income is always green, expenses always red. */
export const chartColors = {
  income: '#1E8E5A', // --ok-500 (tokens.css)
  expense: '#D2453C', // --bad-500 (tokens.css)
  neutral: '#A66A10', // --action / saffron primary (tokens.css + ThemeContext saffron preset)
  comparative1: '#7E7261', // saffron preset neutral.500 (ThemeContext.tsx)
  comparative2: '#9FC0E4', // --info-300 (tokens.css)
} as const;

/**
 * Categorical palette for multi-series charts (channel stacks, pies).
 * Every entry is an existing Tier-1 token — muted, warm, in-family.
 */
export const chartPalette: string[] = [
  '#A66A10', // --action (tokens.css)
  '#7E7261', // saffron preset neutral.500 (ThemeContext.tsx)
  '#9FC0E4', // --info-300 (tokens.css)
  '#9ED4B6', // --ok-300 (tokens.css)
  '#F0C285', // --warn-300 (tokens.css)
  '#6D5BD0', // --violet-500 (tokens.css)
  '#EDA49E', // --bad-300 (tokens.css)
  '#2D6CB5', // --info-500 (tokens.css)
];

/** Resting fill opacity for series; hover goes fully opaque. */
export const SERIES_FILL_OPACITY = 0.9;
export const SERIES_HOVER_OPACITY = 1;

/** Spread onto <Bar> so the hovered bar snaps to full opacity. */
export const activeBarProps = {
  activeBar: { fillOpacity: SERIES_HOVER_OPACITY },
} as const;

/**
 * Spread onto <CartesianGrid>: solid warm hairline, horizontal lines only.
 * (Horizontal-layout bar charts should override to horizontal={false}.)
 */
export const gridProps = {
  stroke: '#E3DCCF', // saffron preset neutral.200 / card border (ThemeContext.tsx)
  strokeDasharray: '', // solid hairline — never dashed
  vertical: false,
} as const;

/** Quiet axis text: small, warm grey, no axis/tick lines. */
export const axisTick = {
  fontSize: 12,
  fill: '#7E7261', // saffron preset neutral.500 (ThemeContext.tsx)
} as const;

/** Spread onto <XAxis>/<YAxis> alongside tick={axisTick}. */
export const quietAxisProps = {
  axisLine: false,
  tickLine: false,
} as const;

/** contentStyle for <Tooltip> — surface card with soft elevation. */
export const tooltipContentStyle: CSSProperties = {
  background: '#FFFFFF', // --n-0 / bg-surface (tokens.css)
  border: '1px solid #E3DCCF', // saffron preset neutral.200 (ThemeContext.tsx)
  borderRadius: 10, // --r-md (tokens.css)
  fontSize: 13,
  boxShadow: '0 8px 24px rgba(27,23,18,.12)', // ≈ --e-3 elevation (tokens.css)
};

/** Rounded bar tops for vertical bars… */
export const barRadius: [number, number, number, number] = [6, 6, 0, 0];
/** …and rounded right ends for horizontal-layout bars. */
export const barRadiusHorizontal: [number, number, number, number] = [0, 6, 6, 0];
/** Bars never balloon on sparse data. */
export const MAX_BAR_SIZE = 48;

/** Spread onto <Legend>. */
export const legendProps = {
  wrapperStyle: { fontSize: 12 },
  iconType: 'circle',
} as const;
